import {Injectable} from '@angular/core';
import {
  AuditLogPage,
  Customer,
  CustomerAdd,
  CustomerPut,
  CustomerService as Api,
  LegalPersonSimple,
  WebshopCustomer
} from '../../generated/customer-client';
import {ValidationService} from '../validation/validation.service';
import {AuthService} from '../auth/auth.service';
import {Observable, tap} from 'rxjs';
import {SnackService} from '../snack/snack.service';
import {PageableResult, ScrollLoadService} from '../../components/scroll-load-component/scroll-load.component';
import {ScrollLoad} from '../../components/scroll-load-component/ScrollLoad';

@Injectable()
export class CustomerService implements ScrollLoadService<Customer> {

  constructor(private api: Api,
              private snackService: SnackService,
              public authService: AuthService) { }

  public search(data: any): Observable<PageableResult<Customer>> {
    return this.api.customersFind(
      data.name || null,
      data.email || null,
      data.search || null,
      data.legalPersonId || null,
      data.page,
      data.pageSize,
      data.sortBy,
      data.sortOrder
    ).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    ) as Observable<PageableResult<Customer>>;
  }
  public displayScrollLoadOption(option: Customer): string {
    return '<b>' + option.firstName + ' ' + option.lastName + '</b>&nbsp;<i>(' + option.email +
      (option.phoneNr ? ', ' + option.phoneNr.split('').join('&#8203;') : '') + ')</i>';
  }
  public displayScrollLoadSelection(selection: Customer): string | undefined {
    if (!selection) return '';

    const customerDetails = [];
    if (selection.email) {
      customerDetails.push(selection.email);
    }
    if (selection.phoneNr) {
      customerDetails.push(selection.phoneNr);
    }
    let displayCustomerName = (selection.firstName || '')
      .concat(' ')
      .concat(selection.lastName || '');
    if (customerDetails.length) {
      displayCustomerName = displayCustomerName
        .concat(' (')
        .concat(customerDetails.join(', '))
        .concat(')');
    }
    return displayCustomerName;
  }
  public getScrollLoad(pageSize?: number, idKey?: string): ScrollLoad<Customer> {
    return new ScrollLoad<Customer>(pageSize || 50, idKey || 'uuid', 'search');
  }

  public get(uuid: string): Observable<Customer> {
    return this.api.customerFindByUuid(uuid).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public add(customer: CustomerAdd): Observable<Customer> {
    return this.api.customerAdd(customer).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public update(uuid: string, data: CustomerPut): Observable<Customer> {
    return this.api.customerPutById(uuid, data).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public searchFromWebshop(email: string): Observable<WebshopCustomer[]> {
    return this.api.customersWebshopGet(email).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getLegals(): Observable<LegalPersonSimple[]> {
    return this.api.findAllLegalsWithCustomers().pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getAuditLogs(entityId: string, page: number, pageSize: number): Observable<AuditLogPage> {
    return this.api.getCustomerAuditLogsByCustomerUuid(entityId, page, pageSize).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public validateCustomer(customer: Customer, validateLegal: boolean = false) {
    if (!customer) {
      return false;
    }
    const name = customer.firstName && ValidationService.validateString(customer.firstName, 2) &&
      customer.lastName && ValidationService.validateString(customer.lastName, 2);
    const email = !customer.email || ValidationService.validateEmail(customer.email);
    const phone = !customer.phoneNr || ValidationService.validatePhone(customer.phoneNr);
    let legalPersons = true;
    if (validateLegal) {
      legalPersons = customer.legalPersons && !customer.legalPersons.filter(lp => {
        return !lp.id;
      }).length;
    }
    return name && email && phone && legalPersons;
  }
}
